import qs from 'qs';

import { type DamLibrarySort, DEFAULT_DAM_LIBRARY_SORT, findDamLibrarySort, isDamLibrarySortAvailable } from './damLibrarySorts';

/** Attribute name → selected values, sent as repeatable `filter=attribute=value`. */
type DamFilterValues = Record<string, string[]>;

type DamLibraryViewMode = 'grid' | 'list';

/** Bookmarkable library state; kept in the URL so users return to it from the detail page. */
interface DamLibraryUrlState {
    keyword: string;
    sort: DamLibrarySort;
    page: number;
    filters: DamFilterValues;
    /** Only set when the URL names it; otherwise the user preference decides (D-036). */
    view?: DamLibraryViewMode;
}

const FILTER_SEPARATOR = '=';

const asList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
    }
    return typeof value === 'string' && value.length > 0 ? [value] : [];
};

const parseFilters = (entries: string[]): DamFilterValues => {
    const filters: DamFilterValues = {};
    for (const entry of entries) {
        const separator = entry.indexOf(FILTER_SEPARATOR);
        if (separator <= 0 || separator === entry.length - 1) {
            continue;
        }
        const attribute = entry.slice(0, separator);
        const value = entry.slice(separator + 1);
        const values = filters[attribute] ?? [];
        if (!values.includes(value)) {
            filters[attribute] = [...values, value];
        }
    }
    return filters;
};

const toFilterParams = (filters: DamFilterValues): string[] =>
    Object.entries(filters).flatMap(([attribute, values]) => values.map((value) => `${attribute}${FILTER_SEPARATOR}${value}`));

const parseDamLibraryUrlState = (search: string): DamLibraryUrlState => {
    const params = qs.parse(search, { ignoreQueryPrefix: true });
    const sort = findDamLibrarySort(typeof params.sort === 'string' ? params.sort : undefined);
    const page = Number(params.page);

    return {
        keyword: typeof params.q === 'string' ? params.q.trim() : '',
        sort: sort && isDamLibrarySortAvailable(sort) ? sort.key : DEFAULT_DAM_LIBRARY_SORT,
        page: Number.isInteger(page) && page > 1 ? page : 1,
        filters: parseFilters(asList(params.filter)),
        view: params.view === 'list' || params.view === 'grid' ? params.view : undefined,
    };
};

const toDamLibraryUrlParams = ({ keyword, sort, page, filters, view }: DamLibraryUrlState): Record<string, string | string[]> => {
    const params: Record<string, string | string[]> = {};
    if (keyword) {
        params.q = keyword;
    }
    if (sort !== DEFAULT_DAM_LIBRARY_SORT) {
        params.sort = sort;
    }
    if (page > 1) {
        params.page = String(page);
    }
    const filterParams = toFilterParams(filters);
    if (filterParams.length > 0) {
        params.filter = filterParams;
    }
    if (view) {
        params.view = view;
    }
    return params;
};

const toDamLibrarySearch = (state: DamLibraryUrlState): string => {
    const search = qs.stringify(toDamLibraryUrlParams(state), { arrayFormat: 'repeat' });
    return search ? `?${search}` : '';
};

const countFilterValues = (filters: DamFilterValues): number =>
    Object.values(filters).reduce((sum, values) => sum + values.length, 0);

export { countFilterValues, parseDamLibraryUrlState, toDamLibrarySearch, toDamLibraryUrlParams, toFilterParams };
export type { DamFilterValues, DamLibraryUrlState, DamLibraryViewMode };
